import { useEffect, useState } from 'react';
import { api } from '../../shared/api/client';
import { Button } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';

type Book = { code: string; name: string; testament: 'OT' | 'NT'; chapters: number };

export const splitVerses = (text: string | null) => (text ?? '').split(/[;\n]/).map((r) => r.trim()).filter(Boolean);

/** Arma la cita: libro completo, capítulo, un versículo o un rango. */
export function citation(book: string, chapter: number | null, from: number | null, to: number | null): string {
  if (chapter === null) return book;
  if (from === null) return `${book} ${chapter}`;
  return to !== null && to > from ? `${book} ${chapter}:${from}-${to}` : `${book} ${chapter}:${from}`;
}

/**
 * Selector de versículos a prueba de errores: libro → capítulo (o libro completo) → versículos desde/hasta.
 * Cada cita queda como etiqueta con ✕. También se puede escribir a mano, pero solo se agrega si es válida.
 * El valor se manda en un campo oculto «verses» (citas separadas por «; »).
 */
export function VersePicker({ initial, invalid = [] }: { initial: string | null; invalid?: string[] }) {
  const [items, setItems] = useState<string[]>(splitVerses(initial));
  const add = (text: string) => {
    setItems((xs) => (xs.some((x) => x.toLowerCase() === text.toLowerCase()) ? xs : [...xs, text]));
  };
  return (
    <fieldset className="verse-picker">
      <legend>Versículos a usar</legend>
      <input type="hidden" name="verses" value={items.join('; ')} />
      {items.length ? (
        <ul className="verse-picker__chips">
          {items.map((x) => (
            <li key={x} className={`verse-chip${invalid.includes(x) ? ' verse-chip--bad' : ''}`}>
              {invalid.includes(x) ? '⚠ ' : ''}
              {x}
              <button type="button" aria-label={`Quitar ${x}`} onClick={() => setItems((xs) => xs.filter((y) => y !== x))}>
                ✕
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="small muted">Aún no hay versículos.</p>
      )}
      {items.some((x) => invalid.includes(x)) ? <p className="small text-danger">Las marcadas con ⚠ no son citas válidas: quítalas y vuelve a agregarlas con el selector.</p> : null}
      <CitationChooser onAdd={add} />
    </fieldset>
  );
}

/** Libro → capítulo (o libro completo) → versículos desde/hasta, o la cita escrita (solo si es válida). */
export function CitationChooser({ onAdd, action = 'Agregar', idPrefix = 'vp' }: { onAdd: (citation: string) => void; action?: string; idPrefix?: string }) {
  const books = useAsync((signal) => api.get<{ items: Book[] }>('/bible/books', signal).then((r) => r.items), []);
  const [book, setBook] = useState('');
  const [chapter, setChapter] = useState<number | null>(null);
  const [from, setFrom] = useState<number | null>(null);
  const [to, setTo] = useState<number | null>(null);
  const [verseCount, setVerseCount] = useState<number | null>(null);
  const [typed, setTyped] = useState('');
  const [typedError, setTypedError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const selected = books.status === 'ready' ? books.data.find((b) => b.code === book) : undefined;

  // Cuántos versículos tiene el capítulo elegido (de la traducción importada).
  useEffect(() => {
    setVerseCount(null);
    if (!selected || chapter === null) return;
    const ctrl = new AbortController();
    api
      .get<{ verses: { verse: number }[] }>(`/bible/passage?ref=${encodeURIComponent(`${selected.name} ${chapter}`)}`, ctrl.signal)
      .then((p) => setVerseCount(p.verses.length ? Math.max(...p.verses.map((v) => v.verse)) : null))
      .catch(() => undefined);
    return () => ctrl.abort();
  }, [selected?.name, chapter]);

  async function addTyped() {
    const text = typed.trim();
    if (!text) return;
    setChecking(true);
    setTypedError(null);
    try {
      const p = await api.get<{ label: string }>(`/bible/passage?ref=${encodeURIComponent(text)}`);
      // Sin capítulo es un libro completo (KEORA lo abre en el 1): se guarda solo el nombre del libro.
      const whole = !/\d[\d:.,\s\-–]*$/.test(text);
      onAdd(whole ? p.label.replace(/\s+1$/, '') : p.label);
      setTyped('');
    } catch {
      setTypedError('No reconozco esa cita. Escríbela como «Lamentaciones 3:21-26», «Salmos 23» o «Lamentaciones» (libro completo).');
    } finally {
      setChecking(false);
    }
  }

  const range = (n: number) => Array.from({ length: n }, (_, i) => i + 1);
  return (
    <div className="citation-chooser">
      <div className="verse-picker__row">
        <div className="field">
          <label htmlFor={`${idPrefix}-book`}>Libro</label>
          <select
            id={`${idPrefix}-book`}
            value={book}
            onChange={(e) => {
              setBook(e.target.value);
              setChapter(null);
              setFrom(null);
              setTo(null);
            }}
          >
            <option value="">Escoge…</option>
            {books.status === 'ready'
              ? (['OT', 'NT'] as const).map((t) => (
                  <optgroup key={t} label={t === 'OT' ? 'Antiguo Testamento' : 'Nuevo Testamento'}>
                    {books.data
                      .filter((b) => b.testament === t)
                      .map((b) => (
                        <option key={b.code} value={b.code}>
                          {b.name}
                        </option>
                      ))}
                  </optgroup>
                ))
              : null}
          </select>
        </div>
        <div className="field">
          <label htmlFor={`${idPrefix}-chapter`}>Capítulo</label>
          <select
            id={`${idPrefix}-chapter`}
            disabled={!selected}
            value={chapter ?? ''}
            onChange={(e) => {
              setChapter(e.target.value ? Number(e.target.value) : null);
              setFrom(null);
              setTo(null);
            }}
          >
            <option value="">Libro completo</option>
            {selected ? range(selected.chapters).map((n) => <option key={n} value={n}>{n}</option>) : null}
          </select>
        </div>
        <div className="field">
          <label htmlFor={`${idPrefix}-from`}>Desde el versículo</label>
          <select id={`${idPrefix}-from`} disabled={chapter === null} value={from ?? ''} onChange={(e) => { setFrom(e.target.value ? Number(e.target.value) : null); setTo(null); }}>
            <option value="">Todo el capítulo</option>
            {chapter !== null ? range(verseCount ?? 176).map((n) => <option key={n} value={n}>{n}</option>) : null}
          </select>
        </div>
        <div className="field">
          <label htmlFor={`${idPrefix}-to`}>Hasta</label>
          <select id={`${idPrefix}-to`} disabled={from === null} value={to ?? ''} onChange={(e) => setTo(e.target.value ? Number(e.target.value) : null)}>
            <option value="">Solo ese versículo</option>
            {from !== null ? range(verseCount ?? 176).filter((n) => n > from).map((n) => <option key={n} value={n}>{n}</option>) : null}
          </select>
        </div>
      </div>
      <Button type="button" variant="secondary" disabled={!selected} onClick={() => selected && onAdd(citation(selected.name, chapter, from, to))}>
        {selected ? `${action} ${citation(selected.name, chapter, from, to)}` : 'Agregar'}
      </Button>
      <div className="verse-picker__typed">
        <label htmlFor={`${idPrefix}-typed`} className="small">
          O escríbela
        </label>
        <input
          id={`${idPrefix}-typed`}
          list={`${idPrefix}-books`}
          value={typed}
          placeholder="Juan 3:16"
          aria-invalid={typedError ? true : undefined}
          onChange={(e) => {
            setTyped(e.target.value);
            setTypedError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void addTyped();
            }
          }}
        />
        <datalist id={`${idPrefix}-books`}>{books.status === 'ready' ? books.data.map((b) => <option key={b.code} value={b.name} />) : null}</datalist>
        <Button type="button" variant="ghost" busy={checking} disabled={!typed.trim()} onClick={() => void addTyped()}>
          {action}
        </Button>
      </div>
      {typedError ? <p className="small text-danger" role="alert">{typedError}</p> : null}
    </div>
  );
}
